import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import {
  Building2,
  Package,
  TruckIcon as Truck,
  Users,
  MapPin,
  BarChart3,
  TrendingUp,
  LogOut,
  ChevronDown,
  ChevronRight,
  Home,
  ArrowUpCircle,
  CheckCircle,
  Car,
  Network,
  PieChart,
  Archive,
  Factory,
  
} from 'lucide-react';
import { getDocuments } from '../services/firebase';
import { User, Product, Sale } from '../types';
import AddressValidator from './AddressValidator';
import Inventory from './Inventory';
import LoadoutPage from './LoadoutPage';
import SettlementPage from './SettlementPage';
import ProductionPage from './ProductionPage';
// Removed New Sale and Sales History from Portal menu
// import Sales from './Sales';
// import SalesHistory from './SalesHistory';
// import InventoryManagement from './InventoryManagement';
import WarehouseDashboard from './WarehouseDashboard';
import OverviewSection from './portal/OverviewSection';
import DistributionNetworkSection from './portal/DistributionNetworkSection';
import BeatManagementSection from './portal/BeatManagementSection';
import PeopleSection from './portal/PeopleSection';
import VehicleSection from './portal/VehicleSection';
import { seedDailySalesDetails } from '../services/salesTransactions';

const Portal: React.FC = () => {
  const { currentUser, logout } = useAuth();
  const [activeSection, setActiveSection] = useState<string>('overview');
  const [expandedMenus, setExpandedMenus] = useState<{ [key: string]: boolean }>({
    transact: true,
    manage: true,
    reports: true
  });
  
  // Basic data states for overview
  const [salesmen, setSalesmen] = useState<User[]>([]);
  const [products, setProducts] = useState<Product[]>([]);
  const [sales, setSales] = useState<Sale[]>([]);
  const [isSeeding, setIsSeeding] = useState(false);
  const [seedingMessage, setSeedingMessage] = useState('');
  const [stats, setStats] = useState({
    totalSalesmen: 0,
    totalProducts: 0,
    todaySales: 0,
    totalOutlets: 0,
    pendingOrders: 0,
    lowStockItems: 0
  });

  useEffect(() => {
    loadBasicData();
  }, []);

  const loadBasicData = async () => {
    try {
      const [salesmenData, productsData, salesData] = await Promise.all([
        getDocuments('users'),
        getDocuments('products'),
        getDocuments('sales')
      ]);

      const salesmenUsers = salesmenData.filter((user: User) => user.role === 'salesman');
      setSalesmen(salesmenUsers);
      setProducts(productsData as Product[]);
      setSales(salesData as Sale[]);

      // Calculate basic stats
      const todaySales = salesData.reduce((sum: number, sale: Sale) => sum + sale.grandTotal, 0);
      setStats({
        totalSalesmen: salesmenUsers.length,
        totalProducts: productsData.length,
        todaySales: todaySales,
        totalOutlets: 0, // Will be updated by components that have access to outlets
        pendingOrders: 0,
        lowStockItems: 0
      });
    } catch (error) {
      console.error('Error loading basic data:', error);
    }
  };

  const toggleMenu = (menuKey: string) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuKey]: !prev[menuKey]
    }));
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const menuItems = {
    transact: [
      { key: 'production', label: 'Production', icon: Factory },
      { key: 'loadout-stock', label: 'Loadout Stock', icon: ArrowUpCircle },
      { key: 'settle-route', label: 'Settle Route', icon: CheckCircle }
    ],
    manage: [
      { key: 'people', label: 'People', icon: Users },
      { key: 'vehicle', label: 'Vehicle', icon: Car },
      { key: 'products', label: 'Products', icon: Package },
      { key: 'distribution-network', label: 'Distribution Network', icon: Network },
      { key: 'beat-management', label: 'Beat Management', icon: Truck }
    ],
    reports: [
      { key: 'inventory-report', label: 'Inventory', icon: Archive },
      { key: 'sales-report', label: 'Sales', icon: PieChart }
    ]
  };

  const handlePrepareDailySales = async () => {
    setIsSeeding(true);
    setSeedingMessage('Preparing daily sales data...');

    try {
      const today = new Date().toISOString().split('T')[0];
      const warehouseId = currentUser?.warehouseId || 'default-warehouse';
      const result = await seedDailySalesDetails(
        today,
        warehouseId,
        currentUser?.id || 'system'
      );

      if (result.success) {
        setSeedingMessage(`✅ ${result.message}`);
        setTimeout(() => setSeedingMessage(''), 3000);
      } else {
        setSeedingMessage(`❌ ${result.message}`);
        setTimeout(() => setSeedingMessage(''), 5000);
      }
    } catch (error) {
      setSeedingMessage(`❌ Error: ${error}`);
      setTimeout(() => setSeedingMessage(''), 5000);
    } finally {
      setIsSeeding(false);
    }
  };

  const renderPlaceholderContent = (title: string) => (
    <div className="bg-white p-8 rounded-lg shadow-sm border border-gray-200">
      <div className="text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <Package className="w-8 h-8 text-gray-400" />
        </div>
        <h2 className="text-xl font-semibold text-gray-800 mb-2">{title}</h2>
        <p className="text-gray-600">This section is under development</p>
        <p className="text-sm text-gray-500 mt-1">Features coming soon...</p>
      </div>
    </div>
  );

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <OverviewSection
            currentUser={currentUser as User}
            onNavigateToDistributionNetwork={() => setActiveSection('distribution-network')}
          />
        );
      case 'address-validation':
        return <AddressValidator onBack={() => setActiveSection('overview')} />;
      case 'distribution-network':
        return <DistributionNetworkSection />;
      case 'beat-management':
        return <BeatManagementSection />;
      
      // TRANSACT Section
      case 'loadout-stock':
        return <LoadoutPage onBack={() => setActiveSection('overview')} />;
      case 'settle-route':
        return <SettlementPage onBack={() => setActiveSection('overview')} />;
      case 'production':
        return <ProductionPage onBack={() => setActiveSection('overview')} />;
        
      // MANAGE Section  
      case 'products':
        return <Inventory onBack={() => setActiveSection('overview')} />;
      case 'people':
        return <PeopleSection />;
      case 'vehicle':
        return <VehicleSection />;
      
      // REPORTS Section
      case 'inventory-report':
        return <WarehouseDashboard onShowInventory={() => setActiveSection('products')} />;
        
      default:
        return renderPlaceholderContent(
          menuItems.transact.find(item => item.key === activeSection)?.label ||
          menuItems.manage.find(item => item.key === activeSection)?.label ||
          menuItems.reports.find(item => item.key === activeSection)?.label ||
          'Feature'
        );
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex">
      {/* Sidebar - Desktop Optimized */}
      <div className="w-72 bg-white shadow-lg border-r border-gray-200 flex flex-col">
        {/* Logo/Brand */}
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-gray-800">SFA Portal</h1>
              <p className="text-xs text-gray-600">Warehouse Management</p>
            </div>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex-1 py-6">
          <nav className="space-y-2 px-4">
            {/* Overview */}
            <button
              onClick={() => setActiveSection('overview')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                activeSection === 'overview' 
                  ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">Overview</span>
            </button>

            {/* Address Validation */}
            <button
              onClick={() => setActiveSection('address-validation')}
              className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                activeSection === 'address-validation' 
                  ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MapPin className="w-5 h-5" />
              <span className="font-medium">Address Validation</span>
            </button>

            {/* Transact Section */}
            <div className="pt-4">
              <button
                onClick={() => toggleMenu('transact')}
                className="w-full flex items-center justify-between px-3 py-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Package className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wide">TRANSACT</span>
                </div>
                {expandedMenus.transact ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {expandedMenus.transact && (
                <div className="ml-4 mt-1 space-y-1">
                  {menuItems.transact.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveSection(item.key)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.key 
                            ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Manage Section */}
            <div className="pt-2">
              <button
                onClick={() => toggleMenu('manage')}
                className="w-full flex items-center justify-between px-3 py-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Users className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wide">MANAGE</span>
                </div>
                {expandedMenus.manage ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {expandedMenus.manage && (
                <div className="ml-4 mt-1 space-y-1">
                  {menuItems.manage.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveSection(item.key)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.key 
                            ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Reports Section */}
            <div className="pt-2">
              <button
                onClick={() => toggleMenu('reports')}
                className="w-full flex items-center justify-between px-3 py-2 text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <BarChart3 className="w-5 h-5" />
                  <span className="font-semibold text-sm uppercase tracking-wide">REPORTS</span>
                </div>
                {expandedMenus.reports ? (
                  <ChevronDown className="w-4 h-4" />
                ) : (
                  <ChevronRight className="w-4 h-4" />
                )}
              </button>
              
              {expandedMenus.reports && (
                <div className="ml-4 mt-1 space-y-1">
                  {menuItems.reports.map((item) => {
                    const Icon = item.icon;
                    return (
                      <button
                        key={item.key}
                        onClick={() => setActiveSection(item.key)}
                        className={`w-full flex items-center space-x-3 px-3 py-2 rounded-lg transition-colors ${
                          activeSection === item.key 
                            ? 'bg-orange-50 text-orange-700 border border-orange-200' 
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        <span className="text-sm">{item.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          </nav>
        </div>

        {/* User Profile & Logout */}
        <div className="p-4 border-t border-gray-200">
          <div className="flex items-center space-x-3 mb-3">
            <div className="w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-full flex items-center justify-center">
              <span className="text-white font-medium text-sm">
                {currentUser?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-gray-800">{currentUser?.name}</p>
              <p className="text-xs text-gray-600">Portal Administrator</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center space-x-2 px-3 py-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4" />
            <span className="text-sm">Sign Out</span>
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-auto">
        <div className="p-6">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default Portal;
